import React, { useState, useEffect, useCallback, useRef } from 'react';
import styles from './GalleryCarousel.module.css';



const SLIDES = [
  { src: "/images/33-DSC09445.jpg", caption: 'Taproom Floor'    },
  { src: "/images/34-DSC09447.jpg", caption: 'Craft Pours'      },
  { src: "/images/36-DSC09449.jpg", caption: 'The Brew House'   },
  { src: "/images/30-DSC09441.jpg", caption: 'Private Dining'   },
  { src: "/images/29-DSC09440.jpg", caption: 'Evening Ambience' },
];

const AUTOPLAY_MS = 4500;

const ArrowLeft = ({ className }) => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <circle cx="24" cy="24" r="23" stroke="currentColor" strokeWidth="1"/>
    <path d="M31 24H17M17 24L23 18M17 24L23 30" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ArrowRight = ({ className }) => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <circle cx="24" cy="24" r="23" stroke="currentColor" strokeWidth="1"/>
    <path d="M17 24H31M31 24L25 18M31 24L25 30" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const GalleryCarousel = () => {
  const [current,   setCurrent]   = useState(0);
  const [prev,      setPrev]      = useState(null);
  const [direction, setDirection] = useState(1);
  const [animating, setAnimating] = useState(false);
  const [paused,    setPaused]    = useState(false);
  const touchStartX = useRef(null);

  const total = SLIDES.length;
  const pad   = n => String(n + 1).padStart(2, '0');

  const goTo = useCallback((next, dir) => {
    if (animating || next === current) return;
    setDirection(dir);
    setPrev(current);
    setCurrent(next);
    setAnimating(true);
  }, [animating, current]);

  const goNext = useCallback(() => goTo((current + 1) % total,  1),          [current, total, goTo]);
  const goPrev = useCallback(() => goTo((current - 1 + total) % total, -1),  [current, total, goTo]);

  useEffect(() => {
    if (!animating) return;
    const t = setTimeout(() => { setPrev(null); setAnimating(false); }, 900);
    return () => clearTimeout(t);
  }, [animating]);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(goNext, AUTOPLAY_MS);
    return () => clearInterval(t);
  }, [paused, goNext]);

  useEffect(() => {
    const onKey = e => {
      if (e.key === 'ArrowLeft')  goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goNext, goPrev]);

  const onTouchStart = e => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd   = e => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) dx < 0 ? goNext() : goPrev();
    touchStartX.current = null;
  };

  return (
    <section
      className={styles.carouselSection}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-roledescription="carousel"
      aria-label="Berli Brewing Co. gallery"
    >
      <div className={styles.outerWrapper}>

        {/* ── Desktop: Left arrow (hidden on mobile) ── */}
        <button
          className={`${styles.navButton} ${styles.prevButton} ${styles.desktopOnly}`}
          onClick={goPrev}
          aria-label="Previous slide"
          disabled={animating}
        >
          <ArrowLeft className={styles.arrowIcon} />
        </button>

        {/* ── Center ── */}
        <div className={styles.centerContainer}>

          {/* Image */}
          <div
            className={styles.imageWrapper}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            aria-live="polite"
          >
            {prev !== null && (
              <img
                key={`prev-${prev}`}
                src={SLIDES[prev].src}
                alt={SLIDES[prev].caption}
                className={`${styles.carouselImage} ${direction === 1 ? styles.leaveLeft : styles.leaveRight}`}
              />
            )}
            <img
              key={`curr-${current}`}
              src={SLIDES[current].src}
              alt={SLIDES[current].caption}
              className={`${styles.carouselImage} ${
                animating
                  ? direction === 1 ? styles.enterRight : styles.enterLeft
                  : styles.active
              }`}
            />
          </div>

          {/* ── Desktop meta row ── */}
          <div className={`${styles.metaRow} ${styles.desktopOnly}`}>
            <div className={styles.pagination}>
              <span className={styles.digits}>{pad(current)}</span>
              <span className={styles.slash}>/</span>
              <span className={styles.digits}>{pad(total - 1)}</span>
            </div>
            <div className={styles.dividerLine} />
            <span className={styles.caption}>{SLIDES[current].caption}</span>
            <div className={styles.dots} role="tablist" aria-label="Slide indicators">
              {SLIDES.map((_, i) => (
                <button
                  key={i}
                  role="tab"
                  aria-selected={i === current}
                  aria-label={`Go to slide ${i + 1}`}
                  className={`${styles.dot} ${i === current ? styles.dotActive : ''}`}
                  onClick={() => goTo(i, i > current ? 1 : -1)}
                />
              ))}
            </div>
          </div>

          {/* ── Mobile controls row: prev | pagination | caption | next ── */}
          <div className={`${styles.mobileControls} ${styles.mobileOnly}`}>
            <button
              className={styles.navButton}
              onClick={goPrev}
              aria-label="Previous slide"
              disabled={animating}
            >
              <ArrowLeft className={styles.arrowIcon} />
            </button>

            <div className={styles.mobileMeta}>
              <div className={styles.pagination}>
                <span className={styles.digits}>{pad(current)}</span>
                <span className={styles.slash}>/</span>
                <span className={styles.digits}>{pad(total - 1)}</span>
              </div>
              <div className={styles.dividerLine} />
              <span className={styles.caption}>{SLIDES[current].caption}</span>
            </div>

            <button
              className={styles.navButton}
              onClick={goNext}
              aria-label="Next slide"
              disabled={animating}
            >
              <ArrowRight className={styles.arrowIcon} />
            </button>
          </div>

        </div>

        {/* ── Desktop: Right arrow (hidden on mobile) ── */}
        <button
          className={`${styles.navButton} ${styles.nextButton} ${styles.desktopOnly}`}
          onClick={goNext}
          aria-label="Next slide"
          disabled={animating}
        >
          <ArrowRight className={styles.arrowIcon} />
        </button>

      </div>
    </section>
  );
};

export default GalleryCarousel;